import { useEffect, useMemo, useRef, useState } from 'react';
import { useActiveClassroom, type SubPageKey } from '../../context/ActiveClassroomProvider.js';
import { getPageCatalogEntry } from '@classy/shared';
import { CLASSROOM_PAGE_MODULES } from '../../classroomPages/registry.js';
import { useClassroomPages } from '../../hooks/useClassroomPages.js';
import { useRewardsAccess } from '../../rewards/access.js';
import styles from './MmPageNav.module.css';

interface NavItem {
  key: SubPageKey;
  label: string;
  /** Optional leading glyph (e.g. 🔍 for Search). */
  icon?: string;
}

// Live-mode short labels.
const LIVE_LABELS: Record<string, string> = {
  home: 'Home',
  calendar: 'Calendar',
  'day-of-school': 'Day',
  number: 'Number',
  weather: 'Weather',
  feelings: 'Feelings',
  question: 'Question',
  pledges: 'Pledges',
  youtube: 'YouTube',
  links: 'Links',
};

/**
 * Cross-page navigation for the active classroom's morning meeting.
 *
 * Bottom strip (always visible, single row):
 *   Home  |  Morning meeting  |  Search
 *
 * "Morning meeting" collapses every enabled classroom page (calendar,
 * day, number, weather, feelings, question, pledges) into one tab so
 * the bottom strip stays calm during a live meeting. Tapping it
 * toggles a *second* strip above the main one with the individual
 * pages so the teacher can jump directly to any of them mid-meeting.
 *
 * Tap behavior:
 *   - From Home / Search → opens the expanded strip without navigating
 *     (the teacher picks a page from the strip).
 *   - From any MM page → opens the expanded strip; the current page is
 *     highlighted so the teacher can pick a different one.
 *
 * The expanded strip auto-closes when the teacher picks a page,
 * presses Escape, or taps anywhere outside the nav.
 */
export function MmPageNav() {
  const { active, subPage, goTo } = useActiveClassroom();
  const { enabledPages } = useClassroomPages(active?.id);
  const rewardsAccess = useRewardsAccess();
  const [expanded, setExpanded] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const toNavItem = (key: string): NavItem | null => {
    const m = CLASSROOM_PAGE_MODULES.find((mod) => mod.key === key);
    if (!m) return null;
    return { key: m.key as SubPageKey, label: LIVE_LABELS[m.key] ?? m.label };
  };

  // Morning Meeting section pages: enabled pages with morning-meeting placement
  // (Home is pinned-left; standalone class-level pages get their own tabs).
  // These collapse under the single "Morning meeting" tab.
  const mmPages: NavItem[] = useMemo(() => {
    if (!active) return [];
    return enabledPages
      .filter((key) => getPageCatalogEntry(key)?.placement === 'morning-meeting')
      .map(toNavItem)
      .filter((m): m is NavItem => m !== null);
  }, [active, enabledPages]);

  // Standalone class-level pages (YouTube, Links): each gets its own top-level
  // tab between "Morning meeting" and Search.
  const standalonePages: NavItem[] = useMemo(() => {
    if (!active) return [];
    return enabledPages
      .filter((key) => getPageCatalogEntry(key)?.placement === 'standalone')
      .map(toNavItem)
      .filter((m): m is NavItem => m !== null);
  }, [active, enabledPages]);

  // True iff the active subPage is one of the consolidated MM pages —
  // drives the "Morning meeting" tab's selected styling.
  const onMmPage = mmPages.some((p) => p.key === subPage);

  // Auto-close the expanded strip whenever the teacher navigates away
  // (subPage change), and on Escape.
  useEffect(() => {
    setExpanded(false);
  }, [subPage]);

  useEffect(() => {
    if (!expanded) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setExpanded(false);
    }
    function onPointerDown(e: PointerEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setExpanded(false);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [expanded]);

  if (!active) return null;

  return (
    <div
      ref={wrapperRef}
      className={styles.wrapper}
      data-testid="mm-page-nav-wrapper"
      data-expanded={expanded ? 'true' : 'false'}
    >
      {/* Expanded strip — only renders when the teacher has tapped
       * "Morning meeting" AND there's at least one MM page enabled. */}
      {expanded && mmPages.length > 0 ? (
        <nav
          className={styles.subNav}
          aria-label="Morning meeting pages"
          data-testid="mm-page-nav-sub"
        >
          {mmPages.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={styles.subButton}
              aria-current={subPage === key ? 'page' : undefined}
              onClick={() => {
                goTo(key);
                setExpanded(false);
              }}
              data-testid={`mm-nav-${key}`}
            >
              {label}
            </button>
          ))}
        </nav>
      ) : null}

      <nav className={styles.nav} data-testid="mm-page-nav" aria-label="Main navigation">
        <button
          type="button"
          className={styles.button}
          aria-current={subPage === 'home' ? 'page' : undefined}
          onClick={() => goTo('home')}
          data-testid="mm-nav-home"
        >
          Home
        </button>

        {mmPages.length > 0 ? (
          <button
            type="button"
            className={styles.button}
            aria-current={onMmPage ? 'page' : undefined}
            aria-expanded={expanded}
            aria-controls="mm-page-nav-sub"
            onClick={() => setExpanded((v) => !v)}
            data-testid="mm-nav-morning-meeting"
          >
            Morning meeting
          </button>
        ) : null}

        {/* Private Rewards tracker — a top-level tab just before the standalone
         * pages (YouTube/Links), shown only to the accounts that own it. */}
        {rewardsAccess ? (
          <button
            type="button"
            className={styles.button}
            aria-current={subPage === 'rewards' ? 'page' : undefined}
            onClick={() => goTo('rewards')}
            data-testid="mm-nav-rewards"
          >
            Rewards
          </button>
        ) : null}

        {standalonePages.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={styles.button}
            aria-current={subPage === key ? 'page' : undefined}
            onClick={() => goTo(key)}
            data-testid={`mm-nav-${key}`}
          >
            {label}
          </button>
        ))}

        <button
          type="button"
          className={styles.button}
          aria-current={subPage === 'search' ? 'page' : undefined}
          onClick={() => goTo('search')}
          data-testid="mm-nav-search"
        >
          <span className={styles.icon} aria-hidden="true">
            🔍
          </span>
          Search
        </button>
      </nav>
    </div>
  );
}
